import { useParkingaboServerFetch } from '../../../api/ParkingaboApi';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization';
import { ParkingaboLayoutWithHeader } from '../../../components/layout/ParkingaboLayoutWithHeader';
import { Box, Stack, Typography } from '@mui/material';
import {
    MenuListItemColorVariant,
    ParkingaboListItem,
    ParkingaboListItemLabel,
} from '../../../components/ParkingaboMenuListItem';
import {
    currencyCentsToLocalPrice,
    numberToLocalPrice,
} from 'dg-web-shared/lib/NumberFormatter';
import { ParkingaboAsyncLoadedSection } from '../../../components/layout/ParkingaboAsyncLoadedSection';
import {
    StatusBar,
    StatusBarColorVariants,
} from '../../../components/StatusBar';
import { DateTime } from 'luxon';
import { Formatter } from 'dg-web-shared/lib/Date';
import { ParkingaboOverlineList } from '../../../components/layout/ParkingaboOverlineList';
import { ParkingaboRoutedModal } from '../../../components/layout/ParkingaboRoutedModal';
import { Outlet, useOutletContext, useParams } from 'react-router';
import { ErrorAlert } from 'dg-web-shared/common/components/material-ui/ErrorAlert';
import { Fragment } from 'react';
import { ParkingaboUser } from '../../../shared/ParkingaboModels.tsx';
import { PaymentCollectionInfoBanner } from '../../../components/forms/PaymentMethodForm.tsx';
import { FileDownload, Download } from '@mui/icons-material';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';

const enum ReportItemType {
    PRODUCT_PURCHASE = 'PRODUCT_PURCHASE',
    PRODUCT_REFUND = 'PRODUCT_REFUND',
    PRODUCT_SUBSCRIPTION_CHARGE = 'PRODUCT_SUBSCRIPTION_CHARGE',
    OFFSTREET_PURCHASE = 'OFFSTREET_PURCHASE',
    OFFSTREET_REFUND = 'OFFSTREET_REFUND',
    INPAYMENT = 'INPAYMENT',
    MANUAL = 'MANUAL',
    INITIAL_CREDIT = 'INITIAL_CREDIT',
}

interface AccountStatementMonthBucket {
    yearMonth: string;
    isCurrentMonth: boolean;
    items: AccountStatementItem[];
    token: string;
}

interface AccountStatementItem {
    bkBookingId: number;
    type: ReportItemType;
    date: string;
    reportText: string[];
    postings: Posting[];
    saldo: number;
    contractExternalId: string | null;
    yearMonth: string | null;
    token: string;
}

interface Posting {
    amount: number;
    amountRappen: number;
    vatInfo: PostingAmountInfo | null;
}

interface PostingAmountInfo {
    vatAccountTupleId: number;
    vatPercent: number;
    vatNumber: string | null;
    vatCompanyName: string;
}

interface ParkingaboCustomerAccountStatement {
    initialBalance: number;
    monthBuckets: AccountStatementMonthBucket[];
}

export function AccountStatementOutlet() {
    const { user } = useCustomerContext();
    const [productState] =
        useParkingaboServerFetch<ParkingaboCustomerAccountStatement>(
            () => ({
                url: `user/self/monthly-report`,
            }),
            {},
        );

    return (
        <ParkingaboLayoutWithHeader
            backTo={'..'}
            title={
                <Localized
                    de="Kontoauszug"
                    fr="Relevé de compte"
                    it="Estratto conto"
                    en="Account statement"
                />
            }
            noGutter
            scrollable
        >
            <ParkingaboAsyncLoadedSection
                state={productState}
                render={accountStatements => {
                    const monthBuckets = accountStatements.monthBuckets;
                    if (
                        monthBuckets.length < 1 &&
                        accountStatements.initialBalance == 0
                    ) {
                        return (
                            <Stack
                                direction="column"
                                spacing={0}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'stretch',
                                }}
                            >
                                <ParkingaboListItem>
                                    <Typography
                                        sx={{
                                            color: 'text.disabled',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Localized
                                            de="Keine Einträge gefunden."
                                            fr="Aucune entrée trouvée."
                                            it="Nessuna voce trovata."
                                            en="No records found."
                                        />
                                    </Typography>
                                </ParkingaboListItem>
                            </Stack>
                        );
                    }

                    return (
                        <Stack
                            direction="column"
                            spacing={0}
                            sx={{
                                justifyContent: 'flex-start',
                                alignItems: 'stretch',
                            }}
                        >
                            <CollectionInfo
                                user={user}
                                saldo={
                                    monthBuckets[0]?.items[0]?.saldo ??
                                    accountStatements.initialBalance
                                }
                            />
                            {monthBuckets.map(bucket => (
                                <Fragment key={bucket.yearMonth}>
                                    <MonthHeader bucket={bucket} />
                                    {bucket.items.map(item => (
                                        <ParkingaboListItem
                                            variableHeight
                                            key={item.bkBookingId}
                                            to={item.bkBookingId.toString()}
                                        >
                                            <StatementEntry item={item} />
                                        </ParkingaboListItem>
                                    ))}
                                </Fragment>
                            ))}
                            {accountStatements.initialBalance != 0 && (
                                <InitialBalance
                                    initialBalance={
                                        accountStatements.initialBalance
                                    }
                                />
                            )}
                            <AccountStatementDetailOutlet
                                context={{ monthBuckets: monthBuckets }}
                            />
                        </Stack>
                    );
                }}
            />
        </ParkingaboLayoutWithHeader>
    );
}

function MonthHeader({ bucket }: { bucket: AccountStatementMonthBucket }) {
    const { language } = useLanguage();
    const { user } = useCustomerContext();

    const monthLabel = Formatter.monthNameYear(
        DateTime.fromISO(bucket.yearMonth),
        Formatter.getLocaleFromString(language),
    );

    const downloadUrl = `/ui-api/parkingabo/user/self/operator/${user.operatorExternalId}/monthly-report/${bucket.yearMonth.replace('-', '/')}?token=${bucket.token}`;

    if (bucket.isCurrentMonth) {
        return (
            <StatusBar
                key={bucket.yearMonth}
                colorVariant={StatusBarColorVariants.DISABLED}
            >
                {monthLabel}
            </StatusBar>
        );
    } else {
        return (
            <StatusBar
                key={bucket.yearMonth}
                colorVariant={StatusBarColorVariants.DISABLED}
                rightLabel={
                    <Download
                        fontSize="small"
                        sx={{
                            display: 'block',
                        }}
                    />
                }
                onClick={() => {
                    openNewTab(downloadUrl);
                }}
                sx={{ cursor: 'pointer' }}
            >
                {monthLabel}
            </StatusBar>
        );
    }
}

function InitialBalance({ initialBalance }: { initialBalance: number }) {
    return (
        <StatusBar
            colorVariant={StatusBarColorVariants.DISABLED}
            rightLabel={numberToLocalPrice(initialBalance)}
        >
            <Localized
                de="Saldovortrag"
                fr="Report de solde"
                it="Saldo riportato"
                en="Balance carried forward"
            />
        </StatusBar>
    );
}

function CollectionInfo({
    user,
    saldo,
}: {
    user: ParkingaboUser;
    saldo: number;
}) {
    if (
        user.overduePaymentsAmountRappen === 0 ||
        user.overduePaymentsAmountRappen === null
    ) {
        if (saldo >= 0) {
            return null;
        }
        return (
            <PaymentCollectionInfoBanner>
                <Localized
                    de={
                        <>
                            Die nächste Belastung des Zahlungsmittels wird am
                            <b> {user.nextPaymentCollectionDate}</b>{' '}
                            stattfinden.
                        </>
                    }
                    fr={
                        <>
                            Le prochain débit du moyen de paiement aura lieu le
                            <b> {user.nextPaymentCollectionDate}</b>.
                        </>
                    }
                    it={
                        <>
                            Il prossimo addebito del mezzo di pagamento avverrà
                            il <b>{user.nextPaymentCollectionDate}</b>.
                        </>
                    }
                    en={
                        <>
                            The next debit of the mean of payment will take
                            place on <b>{user.nextPaymentCollectionDate}</b>.
                        </>
                    }
                />
            </PaymentCollectionInfoBanner>
        );
    }
    return (
        <PaymentCollectionInfoBanner>
            <Localized
                de={
                    <>
                        Die nächste Belastung wird bei der Registrierung{' '}
                        <b>eines gültigen Zahlungsmittels</b> stattfinden.
                    </>
                }
                fr={
                    <>
                        Le prochain débit aura lieu lors de la registration{' '}
                        <b>d&#39;un moyen de paiement valide</b>.
                    </>
                }
                it={
                    <>
                        Il prossimo addebito avverrà al momento della
                        registrazione di <b>un mezzo di pagamento valido</b>.
                    </>
                }
                en={
                    <>
                        The next charge will be made when registering a{' '}
                        <b>valid mean of payment</b>.
                    </>
                }
            />
        </PaymentCollectionInfoBanner>
    );
}

function StatementEntry({ item }: { item: AccountStatementItem }) {
    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{
                justifyContent: 'space-between',
                width: '100%',
                height: '100%',
            }}
        >
            <div>
                {item.reportText.map((text, index) => (
                    <Typography
                        key={index} // eslint-disable-line react/no-array-index-key
                        component={index === 0 ? 'h3' : 'div'}
                        sx={{
                            fontWeight: index === 0 ? 'bold' : undefined,
                        }}
                    >
                        {text}
                    </Typography>
                ))}
            </div>
            <Stack
                direction="column"
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    height: '100%',
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        fontWeight: 'bold',
                    }}
                >
                    {currencyCentsToLocalPrice(
                        item.postings.reduce(
                            (acc, posting) => acc + 100 * posting.amount,
                            0,
                        ),
                        false,
                    )}
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        fontSize: '13px',
                    }}
                >
                    <Localized
                        de="Saldo "
                        fr="Solde "
                        it="Saldo "
                        en="Balance "
                    />
                    {numberToLocalPrice(item.saldo, false)}
                </Typography>
            </Stack>
        </Stack>
    );
}

interface AccountStatementDetailOutletContext {
    monthBuckets: AccountStatementMonthBucket[];
}

export function useAccountStatementDetailContext() {
    return useOutletContext<AccountStatementDetailOutletContext>();
}

function AccountStatementDetailOutlet({
    context,
}: {
    context: AccountStatementDetailOutletContext;
}) {
    return <Outlet context={context} />;
}

export function AccountStatementItemDetail() {
    const { monthBuckets } = useAccountStatementDetailContext();
    const { bkBookingId } = useParams<{ bkBookingId: string }>();
    const { user } = useCustomerContext();
    const item =
        monthBuckets
            .reduce(
                (acc: AccountStatementItem[], bucket) => [
                    ...acc,
                    ...bucket.items,
                ],
                [],
            )
            .find(
                item =>
                    bkBookingId &&
                    item.bkBookingId === parseInt(bkBookingId, 10),
            ) ?? null;

    if (item === null) {
        return (
            <AccountStatementModal>
                <ErrorAlert>
                    <Localized
                        de="Bewegung nicht gefunden."
                        fr="Mouvement non trouvé."
                        it="Movimento non trovato."
                        en="Movement not found."
                    />
                </ErrorAlert>
            </AccountStatementModal>
        );
    }

    const showVatInfoOnPrice =
        item.postings.length === 1 && item.postings[0].vatInfo !== null;

    return (
        <AccountStatementModal>
            <ParkingaboOverlineList.Body>
                <ParkingaboOverlineList.Item
                    label={
                        <Localized de="Datum" fr="Date" it="Data" en="Date" />
                    }
                >
                    {Formatter.dayMonthYear(DateTime.fromISO(item.date))}
                </ParkingaboOverlineList.Item>
                <ParkingaboOverlineList.Item
                    label={
                        <Localized
                            de="Beschreibung"
                            fr="Description"
                            it="Descrizione"
                            en="Description"
                        />
                    }
                >
                    {item.reportText.map(line => (
                        <div key={line}>{line}</div>
                    ))}
                </ParkingaboOverlineList.Item>
                <ParkingaboOverlineList.Item
                    label={
                        <Localized
                            de="Betrag"
                            fr="Montant"
                            it="Importo"
                            en="Amount"
                        />
                    }
                >
                    {currencyCentsToLocalPrice(
                        item.postings.reduce(
                            (amount, posting) => amount + posting.amountRappen,
                            0,
                        ),
                    )}
                    {showVatInfoOnPrice && (
                        <VatPercentText
                            vatPercent={item.postings[0].vatInfo!.vatPercent}
                        />
                    )}
                </ParkingaboOverlineList.Item>
                <ShowVatInfo
                    postingsWithVat={item.postings.filter(
                        posting => posting.vatInfo,
                    )}
                    vatInfoShownOnPrice={showVatInfoOnPrice}
                />
            </ParkingaboOverlineList.Body>
            {item.contractExternalId && (
                <ReceiptButton
                    url={
                        item.type === ReportItemType.PRODUCT_SUBSCRIPTION_CHARGE
                            ? subscriptionChargeReceiptUrl(
                                  user.operatorExternalId,
                                  item.contractExternalId,
                                  item.yearMonth,
                                  item.token,
                              )
                            : contractReceiptUrl(
                                  user.operatorExternalId,
                                  item.contractExternalId,
                                  item.token,
                              )
                    }
                />
            )}
        </AccountStatementModal>
    );
}

function subscriptionChargeReceiptUrl(
    operatorExternalId: string,
    contractExternalId: string,
    yearMonth: string | null,
    token: string,
) {
    return `/ui-api/parkingabo/user/self/operator/${operatorExternalId}/subscription-charge/${contractExternalId}/year-month/${yearMonth}/receipt/v2?token=${token}`;
}

export function contractReceiptUrl(
    operatorExternalId: string,
    contractExternalId: string,
    token: string,
) {
    return `/ui-api/parkingabo/user/self/operator/${operatorExternalId}/contract/${contractExternalId}/receipt/v2?token=${token}`;
}

export function ReceiptButton({ url }: { url: string }) {
    return (
        <ParkingaboListItem
            colorVariant={MenuListItemColorVariant.PRIMARY}
            onClick={() => {
                openNewTab(url);
            }}
        >
            <ParkingaboListItemLabel
                label={
                    <Localized
                        de="Quittung herunterladen"
                        fr="Télécharge le reçu"
                        it="Scarica ricevuta"
                        en="Download receipt"
                    />
                }
                icon={
                    <FileDownload style={{ fontSize: 36 }} color={'primary'} />
                }
            />
        </ParkingaboListItem>
    );
}

function AccountStatementModal({ children }: { children: React.ReactNode }) {
    return (
        <ParkingaboRoutedModal
            open={true}
            backUrl=".."
            render={() => (
                <ParkingaboLayoutWithHeader
                    title={
                        <Localized
                            de="Kontoauszug"
                            fr="Relevé de compte"
                            it="Estratto conto"
                            en="Account statement"
                        />
                    }
                    scrollable
                    backTo=".."
                >
                    {children}
                </ParkingaboLayoutWithHeader>
            )}
        />
    );
}

function ShowVatInfo({
    postingsWithVat,
    vatInfoShownOnPrice,
}: {
    postingsWithVat: Posting[];
    vatInfoShownOnPrice: boolean;
}) {
    if (postingsWithVat.length < 1) {
        return null;
    }
    return (
        <ParkingaboOverlineList.Item
            label={
                <Localized
                    de="MWST-Info"
                    fr="Info TVA"
                    it="Info IVA"
                    en="VAT info"
                />
            }
        >
            {postingsWithVat.map(posting => {
                const vatInfo = posting.vatInfo!;
                return (
                    <Box
                        key={vatInfo.vatAccountTupleId}
                        sx={{ marginBottom: 2 }}
                    >
                        {!vatInfoShownOnPrice && (
                            <div>
                                {currencyCentsToLocalPrice(
                                    posting.amountRappen,
                                )}
                                <VatPercentText
                                    vatPercent={vatInfo.vatPercent}
                                />
                            </div>
                        )}
                        {vatInfo.vatNumber && <div>{vatInfo.vatNumber}</div>}
                        <div>{vatInfo.vatCompanyName}</div>
                    </Box>
                );
            })}
        </ParkingaboOverlineList.Item>
    );
}

function VatPercentText({ vatPercent }: { vatPercent: number }) {
    return (
        <Localized
            de={` (inkl. ${vatPercent}% MWST)`}
            fr={` (incl. ${vatPercent}% TVA)`}
            it={` (incl. ${vatPercent}% IVA)`}
            en={` (incl. ${vatPercent}% VAT)`}
        />
    );
}
